import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSBlogCategory } from '../../../services';

interface CategoryFormData {
  slug: string;
  nome: string;
  descricao: string;
  cor: string;
  ordem: number;
  status: 'ativo' | 'inativo';
}

const initialFormData: CategoryFormData = {
  slug: '',
  nome: '',
  descricao: '',
  cor: '#1976d2',
  ordem: 0,
  status: 'ativo',
};

const BlogCategories: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<CMSBlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CMSBlogCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; category: CMSBlogCategory | null }>({
    open: false,
    category: null,
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cmsService.getBlogCategories();
      setCategories(response.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingCategories'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleOpenDialog = (category?: CMSBlogCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        slug: category.slug,
        nome: category.nome,
        descricao: category.descricao || '',
        cor: category.cor || '#1976d2',
        ordem: category.ordem,
        status: category.status,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        ...initialFormData,
        ordem: categories.length,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormData(initialFormData);
  };

  const handleChange = (field: keyof CategoryFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'ordem' ? parseInt(value) || 0 : value,
    }));

    // Auto-generate slug
    if (field === 'nome' && !editingCategory) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value),
      }));
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingCategory) {
        await cmsService.updateBlogCategory(editingCategory.id, formData);
        setSuccess(t('cms.categoryUpdated'));
      } else {
        await cmsService.createBlogCategory(formData);
        setSuccess(t('cms.categoryCreated'));
      }
      handleCloseDialog();
      fetchCategories();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingCategory'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.category) return;
    try {
      await cmsService.deleteBlogCategory(deleteDialog.category.id);
      setSuccess(t('cms.categoryDeleted'));
      setDeleteDialog({ open: false, category: null });
      fetchCategories();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingCategory'));
    }
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.blogCategories')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.blogCategoriesSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('cms.newCategory')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.colorColumn')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('cms.slug')}</TableCell>
                <TableCell>{t('cms.articlesCount')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('cms.order')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noCategoryFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: category.cor || '#1976d2',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {category.nome}
                      </Typography>
                      {category.descricao && (
                        <Typography variant="caption" color="text.secondary">
                          {category.descricao.substring(0, 50)}
                          {category.descricao.length > 50 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        /{category.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={category.total_artigos || 0}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={category.status === 'ativo' ? 'success' : 'default'}
                        label={category.status === 'ativo' ? t('common.active') : t('common.inactive')}
                      />
                    </TableCell>
                    <TableCell>{category.ordem}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(category)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, category })}
                          disabled={(category.total_artigos || 0) > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? t('cms.editCategory') : t('cms.newCategory')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.nome}
            onChange={handleChange('nome')}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('cms.slugUrl')}
            value={formData.slug}
            onChange={handleChange('slug')}
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label={t('common.description')}
            value={formData.descricao}
            onChange={handleChange('descricao')}
            margin="normal"
            multiline
            rows={2}
          />
          <Box display="flex" gap={2} mt={2}>
            <TextField
              label={t('cms.colorColumn')}
              type="color"
              value={formData.cor}
              onChange={handleChange('cor')}
              sx={{ width: 100 }}
              InputProps={{
                inputProps: { style: { height: 40, cursor: 'pointer' } },
              }}
            />
            <TextField
              label={t('cms.order')}
              type="number"
              value={formData.ordem}
              onChange={handleChange('ordem')}
              sx={{ width: 100 }}
            />
            <TextField
              select
              label={t('common.status')}
              value={formData.status}
              onChange={handleChange('status')}
              sx={{ flexGrow: 1 }}
              SelectProps={{ native: true }}
            >
              <option value="ativo">{t('common.active')}</option>
              <option value="inativo">{t('common.inactive')}</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.nome || !formData.slug}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, category: null })}
      >
        <DialogTitle>{t('cms.deleteCategory')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteCategory', { name: deleteDialog.category?.nome })}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('cms.actionIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, category: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogCategories;
